import Style from './panelStyles.module.scss';
import { useEditor, useNode } from '@craftjs/core';
import { useMemo, useState, useCallback } from 'react';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { useContextSelector } from 'use-context-selector';
import { TypographyStyles } from 'src/styles';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useSelectNode } from './hooks/useSelectNode';
import { FormFieldProps } from '../../types/BaseControlTypes';
import { LayoutValidationContext, ValidationEnum } from '../../validationContext/layoutValidation.context';
import { dataKeyIsExistsInConditional, validateDataKey } from '../../validationContext/helper';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { AppButton } from 'uikit/buttons';
import { SettingsInput } from './uiKit/input/settingsInput';
import { ReactComponent as CheckIcon } from 'assets/img/generalBuilder/check_24.svg';
import { ReactComponent as ResetIcon } from 'assets/img/generalBuilder/reset_24.svg';
import { ReactComponent as WarningIcon } from 'assets/img/generalBuilder/warning_outline_24.svg';

export const useDataKeyEditing = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Other');
  const { scheme } = useEditor((_, query) => ({
    scheme: query.getSerializedNodes(),
  }));
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as FormFieldProps,
  }));

  const selectNode = useSelectNode();

  const dataKeyValidationError = useContextSelector(LayoutValidationContext, (x) => x.layoutValidationResult[id]);

  const errorMessage = useMemo(
    () => (dataKeyValidationError?.type === ValidationEnum.DataKey ? dataKeyValidationError?.message : undefined),
    [dataKeyValidationError?.message, dataKeyValidationError?.type],
  );

  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const [localDataKey, setLocalDataKey] = useState(props.dataKey);
  const onApplyDataKey = useCallback(() => {
    if (!scheme) return;

    const err = validateDataKey(scheme, localDataKey);
    if (!isNullOrEmpty(err)) {
      setLocalErrorMessage(err!);
      return;
    }

    setProp((p: FormFieldProps) => {
      p.dataKey = localDataKey;
    }, 500);
  }, [localDataKey, scheme, setProp]);

  const { dataKeyFieldIsDisabled, nodes } = useMemo(() => {
    const n = dataKeyIsExistsInConditional(scheme, props.dataKey);
    return { dataKeyFieldIsDisabled: n.length > 0, nodes: n };
  }, [props.dataKey, scheme]);

  return {
    hasError: !!errorMessage || !!localErrorMessage,
    component: (
      <ControlsGroup header={t('DataKey')} contentClassName={Style.dataKeyContainer} tooltip={t('DataKeyTooltip')}>
        {dataKeyFieldIsDisabled ? (
          <div className={Style.dataKeyWithWarning}>
            <div>{props.dataKey}</div>
            <Tooltip
              tooltipContent={
                <div className={Style.dataKeyTooltip}>
                  <div>{t('DataKeyIsUsedInConditionalExpression')}</div>
                  <div className={Style.list}>
                    {nodes.map((node) => (
                      <AppButton
                        key={node.nodeId}
                        variant={'icon-link'}
                        colorSchema={'primary'}
                        textClassName={TypographyStyles.plainText12}
                        text={`• ${node.title}`}
                        onClick={() => selectNode(node.nodeId)}
                        className={Style.S1}
                      />
                    ))}
                  </div>
                </div>
              }
            >
              <WarningIcon className={Style.warningIcon} />
            </Tooltip>
          </div>
        ) : (
          <>
            <SettingsInput
              error={errorMessage ?? localErrorMessage}
              disabled={dataKeyFieldIsDisabled}
              placeholder={t('DataKeyPlaceholder')}
              value={localDataKey}
              onChange={(e) => {
                setLocalDataKey(e.target.value);
                setLocalErrorMessage('');
              }}
              button={
                localDataKey !== props.dataKey
                  ? {
                      colorSchema: 'basic',
                      Icon: ResetIcon,
                      onClick: () => {
                        setLocalDataKey(props.dataKey);
                        setLocalErrorMessage('');
                      },
                    }
                  : undefined
              }
            />
            {localDataKey !== props.dataKey && (
              <AppButton
                colorSchema={'confirm'}
                variant={'icon-link'}
                Icon={CheckIcon}
                onClick={onApplyDataKey}
                className={Style.applyButton}
                disabled={localDataKey === props.dataKey}
              />
            )}
          </>
        )}
      </ControlsGroup>
    ),
  };
};
